import type { AgentInstance, WorkflowInstance } from '@/types/agent';

/**
 * Finds the AgentInstance matching the given agent short name.
 */
export function findAgentInstanceByShortName(
  agentInstances: AgentInstance[],
  agentShortName: string
): AgentInstance | undefined {
  return agentInstances.find(
    (instance) => instance.agent.short_name === agentShortName
  );
}

/**
 * Picks the main workflow instance to run for an agent.
 *
 * Resolution order:
 *  1. If the agent has no workflow instances, return undefined.
 *  2. If a workflow instance is marked as primary (its workflow has no
 *     short_name), return that one.
 *  3. If the agent has exactly one workflow instance, return that one.
 *  4. Otherwise return undefined
 */
export function findPrimaryWorkflowInstance(
  agentInstance: AgentInstance | null | undefined
): WorkflowInstance | undefined {
  const instances = agentInstance?.workflow_instances;
  if (!instances?.length) return undefined;

  const explicit = instances.find((instance) => instance.workflow.short_name === null);
  if (explicit) return explicit;

  if (instances.length === 1) return instances[0];

  return undefined;
}

/**
 * Finds a workflow instance by id, across every agent instance.
 *
 * Callers that hold only an id — such as the chat composer — use this to reach
 * the instance's own settings without refetching it.
 */
export function findWorkflowInstanceById(
  agentInstances: AgentInstance[],
  workflowInstanceId: string | undefined
): WorkflowInstance | undefined {
  if (!workflowInstanceId) return undefined;

  for (const agentInstance of agentInstances) {
    const match = agentInstance.workflow_instances?.find(
      (instance) => String(instance.id) === String(workflowInstanceId)
    );
    if (match) return match;
  }

  return undefined;
}

/**
 * Resolves the primary workflow_instance_id for a given agent short name.
 *
 * Two-step lookup:
 * 1. Find the AgentInstance matching the agent_short_name
 * 2. Find the primary WorkflowInstance (where workflow.short_name === null)
 */
export function resolveWorkflowInstanceId(
  agentInstances: AgentInstance[],
  agentShortName: string
): string | undefined {
  const agentInstance = findAgentInstanceByShortName(agentInstances, agentShortName);
  if (!agentInstance) return undefined;

  const workflowInstance = findPrimaryWorkflowInstance(agentInstance);
  return workflowInstance?.id;
}
